'use client'
import { useTranslations } from 'next-intl'

import useGetCart from '../../lib/hooks/cart/use-get-cart'
import { resolveCheckoutPaymentMethods } from '../../lib/utils/payment-methods'
import { formatPrecioESCustom } from '../../lib/utils/price-formatter'

interface PriceSummaryProps {
  forceSkeleton?: boolean
  location?: string
  country?: string
  island?: string
}

export const PriceSummary: React.FC<PriceSummaryProps> = ({
  forceSkeleton = false,
  location,
  country,
  island,
}) => {
  const t = useTranslations('common.cart')

  // Get cart data with backend summary
  const { data: cartData } = useGetCart({ location, country, island })
  const backendSummary = cartData?.summary
  const availablePaymentMethods = resolveCheckoutPaymentMethods(
    cartData?.available_payment_methods,
    cartData?.shipping_error,
  )
  const isManualOnly = availablePaymentMethods[0] === 'manual'
  const isFullPaymentRequired = availablePaymentMethods.includes('redsys_100')

  // Calculate subtotal from cart items as fallback
  const calculateSubtotal = () => {
    return (
      cartData?.items?.reduce((total, item) => {
        return total + (item.total_price || 0)
      }, 0) || 0
    )
  }

  // Use backend values when available, fallback to calculated values
  const subtotal = backendSummary?.subtotal ?? calculateSubtotal()
  const shippingCost = backendSummary?.shipping ?? null
  const vatRate = backendSummary?.vat_rate ?? 0.21

  // Calculate VAT amount
  const calculatedVatAmount = subtotal * vatRate
  const vatAmount = backendSummary?.vat_amount ?? calculatedVatAmount

  // Calculate totals
  const calculatedTotalWithVATNoShipping = subtotal + calculatedVatAmount
  const calculatedTotalWithVAT
    = shippingCost !== null
      ? subtotal + calculatedVatAmount + shippingCost
      : calculatedTotalWithVATNoShipping
  const totalWithVAT = backendSummary?.total_with_vat ?? calculatedTotalWithVAT

  // Payment requested at confirmation
  const calculatedPaymentConfirmation = totalWithVAT * 0.5
  const paymentConfirmationAmount
    = backendSummary?.payment_confirmation_amount ?? calculatedPaymentConfirmation

  // Format currency
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) {
      return '-'
    }
    return formatPrecioESCustom(amount)
  }

  if (forceSkeleton) {
    return (
      <div className="mb-[43px] mt-3 space-y-3">
        <div className="flex justify-between items-center px-0 mx-0">
          <p className="text-xl font-bold">
            {t('shipping', { defaultValue: 'Envío' })}
          </p>
          <div className="h-6 w-16 rounded bg-stone-100 animate-pulse"></div>
        </div>

        <div className="flex justify-between items-center px-0 mx-0">
          <p className="text-xl font-bold">
            {t('total', { defaultValue: 'Total' })}
            {' '}
            <span className="text-[12px]">
              (
              {t('withVAT', { defaultValue: 'con iva' })}
              )
            </span>
          </p>
          <div className="h-8 w-24 rounded bg-stone-100 animate-pulse"></div>
        </div>

        <div className="flex justify-between items-center px-0 mx-0">
          <p className="text-[17px] max-w-[203px] md:max-w-full">
            {t('paymentConfirmationFull', {
              defaultValue: 'Pago del 100% para confirmación del pedido',
            })}
          </p>
          <div className="h-6 w-16 rounded bg-stone-100 animate-pulse"></div>
        </div>

        <div className="flex justify-between items-center px-0 mx-0">
          <p className="text-[17px]">
            {t('subtotal', { defaultValue: 'Subtotal' })}
          </p>
          <div className="h-6 w-16 rounded bg-stone-100 animate-pulse"></div>
        </div>

        <div className="flex justify-between items-center px-0 mx-0">
          <p className="text-[17px]">
            IVA (
            21
            %)
          </p>
          <div className="h-6 w-16 rounded bg-stone-100 animate-pulse"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-[43px] space-y-3 mt-3">
      {/* Shipping */}
      <div className="flex justify-between items-center px-0 mx-0 ">
        <p className="text-xl font-bold">
          {t('shipping', { defaultValue: 'Envío' })}
        </p>
        <p className="text-[17px]">{formatCurrency(shippingCost)}</p>
      </div>

      {/* Total (with VAT) */}
      <div className="flex justify-between items-center px-0  mx-0 ">
        <p className="text-xl font-bold">
          {t('total', { defaultValue: 'Total' })}
          {' '}
          <span className="text-[12px]">
            (
            {t('withVAT', { defaultValue: 'con iva' })}
            )
          </span>
        </p>
        <p className="text-xl font-bold">{formatCurrency(totalWithVAT)}</p>
      </div>

      {/* Payment for order confirmation */}
      <div className="flex justify-between items-center px-0  mx-0 ">
        <p className="text-[17px] max-w-[203px] md:max-w-full">
          {isManualOnly
            ? t('requestQuote', {
                defaultValue: 'Solicitar pedido sin compromiso',
              })
            : isFullPaymentRequired
              ? t('paymentConfirmationFull', {
                  defaultValue: 'Pago del 100% para confirmación del pedido',
                })
              : t('paymentConfirmation', {
                  defaultValue: 'Pago del 50% para confirmación del pedido',
                })}
        </p>
        <p className="text-[17px] text-nowrap">
          {formatCurrency(isManualOnly ? null : paymentConfirmationAmount)}
        </p>
      </div>

      {/* Subtotal */}
      <div className="flex justify-between items-center px-0  mx-0">
        <p className="text-[17px]">
          {t('subtotal', { defaultValue: 'Subtotal' })}
        </p>
        <p className="text-[17px]">{formatCurrency(subtotal)}</p>
      </div>

      {/* IVA */}
      <div className="flex justify-between items-center px-0  mx-0">
        <p className="text-[17px]">
          IVA (
          {Math.round(vatRate * 100)}
          %)
        </p>
        <p className="text-[17px]">{formatCurrency(vatAmount)}</p>
      </div>
    </div>
  )
}
